import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { isEqual, cloneDeep } from 'lodash';
import { useConst } from '@fiverr-private/hooks';
import { BriefEditor } from '@fiverr-private/ai_brief';
import { useBriefContext } from '@fiverr-private/ai_brief/dist/esm/src/common/BriefContext';
import { RollupAnimation } from '@fiverr-private/dynamic_matching';
import { Divider, Stack } from '@fiverr-private/layout_components';
import { Button } from '@fiverr-private/button';
import { Text } from '@fiverr-private/typography';
import { getContext } from '@fiverr-private/fiverr_context';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { useEditBrief } from '../../hooks/useEditBrief';
import { mixpanelEvents } from '../../monitoring';
import { AppI18n } from '../../i18n';
import { BriefConfirmationScreen, ExtraWaveStatusInfo } from '../BriefConfirmationScreen';
import { useMixpanelRecording } from '../BriefCreationDrawerComponent/useMixpanelRecording';
import { SubmitBriefButton } from './SubmitBriefButton';
import { MissingFieldsButton } from './MissingFieldsButton';
import { RoutingIndicator } from './RoutingIndicator';
import { RncAlert } from './RncAlert';
import { DiscardChangesModal } from './DiscardChangesModal';
import { normalizeSectionForCompare } from './utils/normalizeSectionForCompare';

interface EditStepProps {
  briefId?: string;
  extraWaveStatus?: ExtraWaveStatusInfo;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  fromEditSession?: boolean;
  enableScrollableHeader?: boolean;
}

export const EditStep: React.FC<EditStepProps> = ({
  briefId,
  extraWaveStatus,
  onSuccess,
  onError,
  fromEditSession = false,
  enableScrollableHeader,
}) => {
  const {
    brief,
    setBrief,
    isOpen,
    setIsOpen,
    variant,
    missingFieldsCount,
    isDiscardModalOpen,
    openDiscardModal,
    closeDiscardModal,
  } = useBriefCreationContext();

  useMixpanelRecording({ enabled: fromEditSession });

  const { editedSectionId } = useBriefContext();
  const { isMobile } = getContext();
  const shouldEnableScrollableHeader = enableScrollableHeader ?? isMobile;
  const [hasOpenSectionState, setHasOpenSection] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const isEditMode = !!briefId;
  const useCompactMobileSubmit = !isEditMode && isMobile;

  const initialSections = useConst(() =>
    cloneDeep(
      brief?.sections?.map(({ id, content, included, attachments }) =>
        normalizeSectionForCompare({
          id,
          content,
          included,
          attachments: attachments?.map((a) => a.id),
        })
      )
    )
  );

  const initialTitle = useConst(() => brief?.title);

  const currentSections = useMemo(
    () =>
      brief?.sections?.map(({ id, content, included, attachments }) =>
        normalizeSectionForCompare({
          id,
          content,
          included,
          attachments: attachments?.map((a) => a.id),
        })
      ),
    [brief?.sections]
  );

  const currentTitle = brief?.title;

  const includedInitial = initialSections?.filter((s) => s.included);
  const includedCurrent = currentSections?.filter((s) => s.included);

  const hasBriefChanged = useMemo(
    () => !isEqual(includedCurrent, includedInitial) || currentTitle !== initialTitle,
    [includedCurrent, includedInitial, currentTitle, initialTitle]
  );

  const { handleSubmit: handleEditSubmit, isBriefLoading: isSubmitting } = useEditBrief({
    briefId: briefId || '',
    onSuccess: () => {
      onSuccess?.();
    },
    onError,
    fromEditSession,
  });

  useEffect(() => {
    mixpanelEvents.seamless(variant).editBrief.show({
      type: isEditMode ? 'edit' : 'creation',
      ...(fromEditSession ? { fromEditSession: true } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisabled =
    hasOpenSectionState || !!missingFieldsCount || !!editedSectionId || (isEditMode && !hasBriefChanged);

  const handleSaveAndNext = useCallback(() => {
    setIsConfirmationOpen(true);
  }, []);

  const handleBackToEditing = useCallback(() => {
    setIsConfirmationOpen(false);
  }, []);

  const handleCloseAttempt = useCallback(() => {
    if (isEditMode && hasBriefChanged) {
      openDiscardModal();
    } else {
      setIsOpen(false);
    }
  }, [isEditMode, hasBriefChanged, setIsOpen, openDiscardModal]);

  const handleDiscardConfirm = useCallback(() => {
    closeDiscardModal();
    setIsOpen(false);
  }, [setIsOpen, closeDiscardModal]);

  const handlePostBrief = useCallback(
    async (shareWithMoreFreelancers: boolean) => {
      if (!isEditMode || !brief) return;
      await handleEditSubmit(shareWithMoreFreelancers);
    },
    [isEditMode, brief, handleEditSubmit]
  );

  if (!brief) return null;

  const briefEditorContent = (
    <>
      <BriefEditor
        brief={brief}
        onClose={handleCloseAttempt}
        seller={{} as any}
        subCategoryId={{} as any}
        categoryId={{} as any}
        isBriefDrawerOpen={isOpen}
        onBriefChanged={(brief) => setBrief(brief)}
        componentName="dm_brief_creation"
        source="dm_brief_creation"
        shouldShowSidebar
        withDivider={false}
        allowSaveAfterEditOnly
        onSectionEditStateChange={setHasOpenSection}
        title={
          shouldEnableScrollableHeader ? (
            <Text size="b_lg" fontWeight="bold">
              <AppI18n k={'edit_drawer_header'} />
            </Text>
          ) : undefined
        }
        enableScrollableHeader={shouldEnableScrollableHeader}
        footer={
          <Stack direction="column" gap="0">
            <RncAlert />
            <Divider />
          </Stack>
        }
        buttons={
          <Stack
            width="100%"
            direction="row"
            alignItems="center"
            justifyContent={{ default: 'spaceBetween', sm: 'flexEnd' }}
            wrap={{ default: 'wrap', sm: 'nowrap' }}
          >
            <RoutingIndicator isEditMode={isEditMode} />
            {useCompactMobileSubmit ? (
              <Stack direction="row" gap="2" alignItems="center" flexGrow="1" justifyContent="flexEnd">
                <MissingFieldsButton />
                <SubmitBriefButton compactOnMobile />
              </Stack>
            ) : (
              <>
                <MissingFieldsButton />
                {isEditMode ? (
                  <Stack direction="row" gap="3">
                    <Button intent="secondary" variant="ghost" onClick={handleCloseAttempt}>
                      <AppI18n k="cancel" />
                    </Button>
                    <Button onClick={handleSaveAndNext} disabled={isDisabled}>
                      <AppI18n k="update_brief" />
                    </Button>
                  </Stack>
                ) : (
                  <SubmitBriefButton />
                )}
              </>
            )}
          </Stack>
        }
      />

      {isEditMode && (
        <DiscardChangesModal isOpen={isDiscardModalOpen} onClose={closeDiscardModal} onDiscard={handleDiscardConfirm} />
      )}
    </>
  );

  if (!isEditMode) {
    return briefEditorContent;
  }

  return (
    <RollupAnimation
      height="100%"
      handleHeight={100}
      onStateChange={() => setIsConfirmationOpen(!isConfirmationOpen)}
      isOpen={!isConfirmationOpen}
      backgroundColor="white"
      RollupFront={briefEditorContent}
      RollupBack={
        <BriefConfirmationScreen
          extraWaveStatus={extraWaveStatus}
          onBackToEditing={handleBackToEditing}
          onPostBrief={handlePostBrief}
          isSubmitting={isSubmitting}
        />
      }
    />
  );
};
